// smartreader-frontend/src/services/auth-service.js
import { AUTH_TOKEN_KEY } from '../auth/storage-keys';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const handleNetworkError = (error) => {
  if (!error.userMessage) {
    error.userMessage = 'Network error. Please check your connection.';
  }
  throw error;
};

const handleResponse = async (response) => {
  if (response.ok) {
    return response.json();
  }

  const error = new Error('Request failed');
  let message;
  try {
    const data = await response.json();
    message = data?.message;
  } catch (_) {
    // ignore non-JSON responses
  }

  if (message) {
    error.userMessage = message;
  } else if (response.status >= 500) {
    error.userMessage = 'Server error. Please try again later.';
  } else {
    error.userMessage = 'Request failed. Please check your input.';
  }

  throw error;
};

export const authService = {
  login: async (username, password) => {
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        // No x-api-key (#1778): login is @Public and has needed no key since #1279.
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Enable cookies
        body: JSON.stringify({ username, password }),
      });
      const data = await handleResponse(response);

      // Remove any legacy localStorage token
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.removeItem(AUTH_TOKEN_KEY);
      }

      // Token is now in httpOnly cookie, return success message
      return data.message || 'Login successful';
    } catch (error) {
      handleNetworkError(error);
    }
  },

  logout: async () => {
    try {
      const response = await fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Send cookies with request
      });

      // Clear any legacy localStorage token
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.removeItem(AUTH_TOKEN_KEY);
      }

      const data = await handleResponse(response);
      return data.message || 'Logout successful';
    } catch (error) {
      // Even if logout fails, clear local state
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.removeItem(AUTH_TOKEN_KEY);
      }
      handleNetworkError(error);
    }
  },

  fetchCurrentUser: async () => {
    try {
      // The cookie alone (#1778). With the key, an expired cookie used to come
      // back as the key's identity instead of 401.
      const response = await fetch(`${API_URL}/auth/me`, {
        credentials: 'include', // Send cookies with request
      });

      // 401/403: not authenticated — return null role instead of throwing to avoid
      // console noise and unhandled rejections when checking session on load.
      if (response.status === 401 || response.status === 403) {
        return { role: null, username: null, appMode: null };
      }

      const data = await handleResponse(response);
      return { role: data.role, username: data.username, appMode: data.appMode };
    } catch (error) {
      handleNetworkError(error);
    }
  },
};

export default authService;

